/**
 * TenantsPage - Tenant list, filter/search, and create flow
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, MoreVertical, Building2, Users } from 'lucide-react';
import { LoadingState, StatusBadge, ConfirmDialog, Toast } from '../components/common';
import { CreateTenantModal } from '../components/tenants/CreateTenantModal';
import { useTenantsPage, type TenantStatusFilter } from '../hooks/useTenantsPage';

const FILTER_TABS: TenantStatusFilter[] = ['all', 'active', 'inactive'];

export function TenantsPage() {
  const navigate = useNavigate();
  const { query, filters, modal, mutations, toast } = useTenantsPage();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<{ id: string; name: string; nextStatus: 'active' | 'inactive' } | null>(null);

  if (query.hasError) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6">
        <p className="text-red-800 dark:text-red-200">Failed to load tenants</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <LoadingState isLoading={query.isLoading} message="Loading tenants..." />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-subtitle text-gray-900 dark:text-white">Tenant Management</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage all tenant accounts</p>
        </div>
        <button
          onClick={modal.open}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-all flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Tenant
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl">
              <Building2 className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Active Tenants</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{query.counts.active}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
              <Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{query.counts.totalUsers}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and search */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter:</span>
            {FILTER_TABS.map((status) => (
              <button
                key={status}
                onClick={() => filters.setStatusFilter(status)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  filters.statusFilter === status
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>

          <div className="flex-1 md:max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or domain..."
                value={filters.searchQuery}
                onChange={(e) => filters.setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 transition-all"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tenant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Users</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Projects</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {query.tenants.length === 0 && !query.isLoading && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    No tenants found
                  </td>
                </tr>
              )}
              {query.tenants.map((tenant) => (
                <tr
                  key={tenant.id}
                  onClick={() => navigate(`/tenants/${tenant.id}`)}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                >
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{tenant.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{tenant.domain}</div>
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={tenant.status} />
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{tenant.userCount}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{tenant.projectCount}</td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {new Date(tenant.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId(openMenuId === tenant.id ? null : tenant.id);
                        }}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                      >
                        <MoreVertical className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                      </button>

                      {openMenuId === tenant.id && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId(null);
                            }}
                          />
                          <div
                            className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-700 rounded-xl shadow-lg border border-gray-200 dark:border-gray-600 py-1 z-20"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => {
                                setOpenMenuId(null);
                                navigate(`/tenants/${tenant.id}`);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                            >
                              View Overview
                            </button>
                            <button
                              onClick={() => {
                                setOpenMenuId(null);
                                navigate(`/tenants/${tenant.id}?tab=settings`);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                            >
                              Edit Settings
                            </button>
                            <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
                            <button
                              onClick={() => {
                                setOpenMenuId(null);
                                setConfirmTarget({
                                  id: tenant.id,
                                  name: tenant.name,
                                  nextStatus: tenant.status === 'active' ? 'inactive' : 'active',
                                });
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            >
                              {tenant.status === 'active' ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CreateTenantModal
        isOpen={modal.isOpen}
        onClose={modal.close}
        onSubmit={(payload) => mutations.create.mutate(payload)}
        isSubmitting={mutations.create.isPending}
      />

      <ConfirmDialog
        isOpen={confirmTarget !== null}
        onClose={() => setConfirmTarget(null)}
        onConfirm={() => {
          if (confirmTarget) {
            mutations.updateStatus.mutate({ tenantId: confirmTarget.id, status: confirmTarget.nextStatus });
          }
        }}
        title={confirmTarget?.nextStatus === 'inactive' ? 'Deactivate Tenant' : 'Activate Tenant'}
        message={
          confirmTarget?.nextStatus === 'inactive'
            ? `Are you sure you want to deactivate "${confirmTarget?.name}"? This can be reversed later.`
            : `Are you sure you want to reactivate "${confirmTarget?.name}"?`
        }
        confirmLabel={confirmTarget?.nextStatus === 'inactive' ? 'Deactivate' : 'Activate'}
        variant={confirmTarget?.nextStatus === 'inactive' ? 'danger' : 'info'}
      />

      <Toast isOpen={toast.isOpen} onClose={toast.close} message={toast.message} variant={toast.variant} />
    </div>
  );
}
